<template>
  <div class="tabs">
    <button
      :class="[
        'tabs__nav-button',
        'tabs__nav-button_prev',
        { 'tabs__nav-button_disabled': !haveScrollLeft }
      ]"
      @click="selectPrev"
    >
      <TabsArrow class="tabs__nav-button-icon" />
    </button>
    <div class="tabs__slider-wrapper">
      <Flickity
        ref="flickity"
        :key="`tabs-slider-${_uid}`"
        :options="sliderOptions"
        :class="[
          'tabs__slider',
          {'tabs__slider_have-scroll-right': showRightGradient},
          {'tabs__slider_have-scroll-left': showLeftGradient}
        ]"
      >
        <button
          v-for="tab in tabs"
          :key="`tab-${tab.value}`"
          :class="[
            'tabs__tab',
            'txt-regular',
            {'tabs__tab_active': activeTabValue === tab.value}
          ]"
          @click="selectTab(tab.value)"
        >
          {{ tab.text }}
        </button>
      </Flickity>
    </div>
    <button
      :class="[
        'tabs__nav-button',
        'tabs__nav-button_next',
        { 'tabs__nav-button_disabled': !haveScrollRight }
      ]"
      @click="selectNext"
    >
      <TabsArrow class="tabs__nav-button-icon" />
    </button>
  </div>
</template>

<script>
  import TabsArrow from '@/assets/img/icons/tabs-arrow.svg?inline'
  import Flickity from 'vue-flickity'
  import { get } from 'lodash'
  import { mapGetters } from 'vuex'

  Flickity.beforeDestroy = undefined // stop breaking when transition

  export default {
    name: 'BaseTabs',
    components: {
      TabsArrow,
      Flickity
    },
    props: {
      tabs: {
        type: Array,
        required: true,
        validator (tabs) {
          return tabs.every(tab => {
            return 'value' in tab &&
              'text' in tab
          })
        }
      }
    },
    data () {
      return {
        sliderOptions: {
          prevNextButtons: false,
          pageDots: false,
          freeScroll: true,
          contain: true,
          cellAlign: 'left'
        },
        activeTabValue: null
      }
    },
    computed: {
      ...mapGetters(['isMobile']),
      activeTabIndex () {
        return this.tabs.indexOf(this.tabs.find(tab => tab.value === this.activeTabValue))
      },
      haveScrollRight () {
        const nextTabIndex = this.activeTabIndex + 1
        return nextTabIndex < this.tabs.length
      },
      haveScrollLeft () {
        const prevTabIndex = this.activeTabIndex - 1
        return prevTabIndex >= 0
      },
      showRightGradient () {
        return this.haveScrollRight && (this.isMobile || this.tabs.length > 2)
      },
      showLeftGradient () {
        return this.haveScrollLeft && (this.isMobile || this.tabs.length > 2)
      }
    },
    mounted () {
      this.selectTab(this.tabs[0].value)
    },
    methods: {
      selectTab (tabValue) {
        this.activeTabValue = tabValue
        this.$emit('tabChange', this.tabs.find(tab => tab.value === tabValue))
        if (this.$refs.flickity) {
          this.$refs.flickity.select(this.activeTabIndex)
        }
      },
      selectPrev () {
        const prevTabValue = get(this.tabs[this.activeTabIndex - 1], 'value')
        this.$emit('tabChangeArrowPrev', this.tabs[this.activeTabIndex - 1])
        if (!this.haveScrollLeft) return
        this.selectTab(prevTabValue)
      },
      selectNext () {
        const nextTabValue = get(this.tabs[this.activeTabIndex + 1], 'value')
        this.$emit('tabChangeArrowNext', this.tabs[this.activeTabIndex + 1])
        if (!this.haveScrollRight) return
        this.selectTab(nextTabValue)
      }
    }
  }
</script>

<style lang="scss">
  .tabs {
    position: relative;

    @include mobile {
      margin-bottom: 25px;
    }

    &__nav-button {
      position: absolute;
      top: -6px;
      width: 48px;
      height: 48px;
      padding: 0;
      cursor: pointer;
      background: none;
      border: 0;
      outline: none;
      transition: opacity 0.3s;

      @include tablet {
        top: -12px;
      }

      @include mobile {
        top: 0;
      }

      &-icon {
        width: 100%;
        height: 100%;
        stroke: $c-gray-dark;
      }

      @include hover {
        opacity: 1;
      }

      &_prev {
        left: -48px;

        @include tablet {
          left: -37px;
        }

        @include mobile {
          left: -16px;
        }
      }

      &_next {
        right: -48px;
        transform: rotate(180deg);

        @include tablet {
          right: -37px;
        }

        @include mobile {
          right: -16px;
        }
      }

      &_disabled {
        cursor: default;
        opacity: 0.3 !important;
      }
    }

    &__slider {
      position: relative;
      outline: none;

      @include tablet {
        margin: 0 20px;
      }

      &::after,
      &::before {
        position: absolute;
        top: 0;
        z-index: 4;
        display: none;
        width: 15px;
        height: 100%;
        content: "";
        opacity: 0;
        transition: opacity 0.3s;

        @include mobile {
          display: block;
        }
      }

      &::after {
        right: 0;
        background: linear-gradient(to right, rgba(255, 255, 255, 0), $c-gray-light);
      }

      &::before {
        left: 0;
        background: linear-gradient(to left, rgba(255, 255, 255, 0), $c-gray-light);
      }

      &_have-scroll-right {
        &::after {
          opacity: 1;
        }
      }

      &_have-scroll-left {
        &::before {
          opacity: 1;
        }
      }
    }

    &__tab {
      padding: 0 15px 25px 15px;
      font-family: "FavoritBookC", Arial, Helvetica, sans-serif;
      color: rgba($c-black, 0.5);
      white-space: nowrap;
      cursor: pointer;
      background-color: $c-white;
      border: none;
      outline: none;
      transition: color 0.3s;

      @include mobile {
        padding: 13px 24px;
        font-size: 14px !important;
        line-height: 24px !important;
        color: $c-black;
        background-color: rgba($c-black, 0.08);
        border-radius: 71px;

        &:not(:last-child) {
          margin-right: 12px;
        }
      }

      @include hover {
        color: $c-black;
      }

      &::after {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 0;
        height: 1px;
        content: '';
        background-color: $c-black;
        transition: width 0.3s ease-in-out;

        @include mobile {
          display: none;
        }
      }

      &_active {
        position: relative;
        color: $c-black;

        @include mobile {
          color: $c-white;
          background-color: $c-black;
        }

        &::after {
          width: 100%;
        }
      }
    }
  }
</style>
